/**
 * Base-level default payment schedule templates.
 * Stored on `_bases/{baseKey}.documentPayments`.
 */

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {unknown} raw
 * @returns {{
 *   amount: number|null,
 *   amountBasis: 'fixed'|'percent',
 *   percentOfTotal: number|null,
 *   title: string,
 *   paymentTerms: string,
 * }[]}
 */
export function normalizeDefaultSchedule(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const amountBasis = normalizeString(row.amountBasis).toLowerCase() === 'percent'
        ? 'percent'
        : 'fixed';
      const amount = row.amount == null || row.amount === ''
        ? null
        : Math.round(Number(row.amount));
      const percentOfTotal = row.percentOfTotal == null || row.percentOfTotal === ''
        ? null
        : Number(row.percentOfTotal);
      return {
        title: normalizeString(row.title) || 'Payment',
        amountBasis,
        amount: Number.isFinite(amount) ? amount : null,
        percentOfTotal: Number.isFinite(percentOfTotal) ? percentOfTotal : null,
        paymentTerms: normalizeString(row.paymentTerms),
      };
    })
    .filter(Boolean);
}

/**
 * @param {object|null|undefined} base
 * @returns {{ defaultSchedule: ReturnType<typeof normalizeDefaultSchedule> }}
 */
export function getDocumentPaymentSettings(base) {
  const source = base?.documentPayments && typeof base.documentPayments === 'object'
    ? base.documentPayments
    : {};
  return {
    defaultSchedule: normalizeDefaultSchedule(source.defaultSchedule),
  };
}

/**
 * @param {object[]} scheduleRows - Payment nodes or row models with amount fields
 * @param {function} readField - (payment, fieldName) => value
 * @param {object|null|undefined} [existingRaw] - Current `base.documentPayments`
 * @returns {{ documentPayments: object }}
 */
export function buildDefaultScheduleSettingsPatch(scheduleRows, readField, existingRaw = null) {
  const existing = existingRaw && typeof existingRaw === 'object' ? existingRaw : {};
  const defaultSchedule = (Array.isArray(scheduleRows) ? scheduleRows : []).map((payment, index) => {
    const amountBasis = normalizeString(readField(payment, 'amountBasis')).toLowerCase() === 'percent'
      ? 'percent'
      : 'fixed';
    const amountRaw = readField(payment, 'amount');
    const percentRaw = readField(payment, 'percentOfTotal');
    return {
      title: normalizeString(payment?.title) || `Payment ${index + 1}`,
      amountBasis,
      amount: amountRaw == null || amountRaw === '' ? null : Math.round(Number(amountRaw)),
      percentOfTotal: percentRaw == null || percentRaw === '' ? null : Number(percentRaw),
      paymentTerms: normalizeString(readField(payment, 'paymentTerms')),
    };
  });

  return {
    documentPayments: {
      ...existing,
      defaultSchedule,
    },
  };
}
